import React, { createContext, useContext, useState, useCallback } from "react";
import { STRINGS } from "./strings";

export type Lang = "ko" | "en";

const STORAGE_KEY = "wooyeon_lang";

function detectInitialLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "ko" || saved === "en") return saved;
  } catch {
    /* localStorage 접근 불가 (SSR/사생활 모드) — 기본값 사용 */
  }
  return "ko";
}

interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  /**
   * 번역 헬퍼. key는 점(.) 구분 경로 (예: "common.submit").
   * 현재 언어 사전에 없으면 ko 사전 → key 원문 순으로 폴백한다.
   * vars가 주어지면 "{name}" 형태 자리표시자를 치환한다.
   */
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function lookup(lang: Lang, key: string): string | undefined {
  const parts = key.split(".");
  let node: any = STRINGS[lang];
  for (const p of parts) {
    if (node == null || typeof node !== "object") return undefined;
    node = node[p];
  }
  return typeof node === "string" ? node : undefined;
}

function interpolate(tpl: string, vars?: Record<string, string | number>): string {
  if (!vars) return tpl;
  return tpl.replace(/\{(\w+)\}/g, (m, name) =>
    name in vars ? String(vars[name]) : m
  );
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectInitialLang);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* 저장 실패는 무시 */
    }
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      const hit = lookup(lang, key) ?? lookup("ko", key) ?? key;
      return interpolate(hit, vars);
    },
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLang must be used within a LanguageProvider");
  }
  return ctx;
}
